import { type JSX } from "react";
import {
  FileQueryFilter,
  FilterLogic,
  MetadataFilter,
  PipelineFilter,
} from "@via/pages/sample-trackers/samples/types/filters.ts";
import FilterTagsRow from "@via/pages/sample-trackers/samples/components/filter-tags-row.tsx";
import {
  formatFilterForDisplay,
  formatFileQueryFilterForDisplay,
  formatPipelineFilterForDisplay,
} from "@via/pages/sample-trackers/samples/utils/filter-helpers.ts";

interface FilterTagsProps {
  metadataFilters: MetadataFilter[];
  metadataLogic: FilterLogic;
  onRemoveMetadata: (index: number) => void;
  onMetadataLogicChange: (logic: FilterLogic) => void;
  onEditMetadata?: (index: number) => void;
  pipelineFilters: PipelineFilter[];
  pipelineLogic: FilterLogic;
  onRemovePipeline: (index: number) => void;
  onPipelineLogicChange: (logic: FilterLogic) => void;
  onEditPipeline?: (index: number) => void;
  fileQueryFilters: FileQueryFilter[];
  fileQueryLogic: FilterLogic;
  onRemoveFileQuery: (index: number) => void;
  onFileQueryLogicChange: (logic: FilterLogic) => void;
  onEditFileQuery?: (index: number) => void;
}

export default function FilterTags({
  metadataFilters,
  metadataLogic,
  onRemoveMetadata,
  onMetadataLogicChange,
  onEditMetadata,
  pipelineFilters,
  pipelineLogic,
  onRemovePipeline,
  onPipelineLogicChange,
  onEditPipeline,
  fileQueryFilters,
  fileQueryLogic,
  onRemoveFileQuery,
  onFileQueryLogicChange,
  onEditFileQuery,
}: FilterTagsProps): JSX.Element {
  const hasAnyFilters =
    metadataFilters.length > 0 ||
    pipelineFilters.length > 0 ||
    fileQueryFilters.length > 0;

  if (!hasAnyFilters) {
    return <></>;
  }

  return (
    <div className="space-y-3">
      {metadataFilters.length > 0 && (
        <FilterTagsRow
          label="Metadata"
          icon="database"
          filters={metadataFilters.map(f => ({
            displayText: formatFilterForDisplay(f),
          }))}
          logic={metadataLogic}
          onRemove={onRemoveMetadata}
          onLogicChange={onMetadataLogicChange}
          onEdit={onEditMetadata}
        />
      )}
      {pipelineFilters.length > 0 && (
        <FilterTagsRow
          label="Run"
          icon="run"
          filters={pipelineFilters.map(f => ({
            displayText: formatPipelineFilterForDisplay(f),
          }))}
          logic={pipelineLogic}
          onRemove={onRemovePipeline}
          onLogicChange={onPipelineLogicChange}
          onEdit={onEditPipeline}
        />
      )}
      {fileQueryFilters.length > 0 && (
        <FilterTagsRow
          label="Reports"
          icon="document"
          filters={fileQueryFilters.map(f => ({
            displayText: formatFileQueryFilterForDisplay(f),
          }))}
          logic={fileQueryLogic}
          onRemove={onRemoveFileQuery}
          onLogicChange={onFileQueryLogicChange}
          onEdit={onEditFileQuery}
        />
      )}
    </div>
  );
}
